import React, { createContext, useCallback, useState, useContext } from 'react';
import api from '../services/api';

interface User {
    id: string;
    avatarUrl: string;
    name: string;
    email: string;
}

interface AuthState {
    token: string;
    user: User;
}

interface SignInCredentials {
    email: string;
    password: string;
}

interface AuthContextData {
    user: User;
    signIn(credentials: SignInCredentials): Promise<void>;
    signOut(): void;
    updateUser(userData: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
    const [authData, setAuthData] = useState<AuthState>(() => {
        const token = localStorage.getItem('@MandeSeguro:token');
        const user = localStorage.getItem('@MandeSeguro:user');

        if (user && token) {
            api.defaults.headers.authorization = `Bearer ${token}`;

            return { token, user: JSON.parse(user) };
        }

        return {} as AuthState;
    });

    const signIn = useCallback(async ({ email, password }) => {
        const response = await api.post('sessions', {
            email,
            password,
        });

        const { token, user } = response.data;

        localStorage.setItem('@MandeSeguro:token', token);
        localStorage.setItem('@MandeSeguro:user', JSON.stringify(user));

        api.defaults.headers.authorization = `Bearer ${token}`;

        setAuthData({ token, user });
    }, []);

    const updateUser = useCallback(
        (userData: User) => {
            localStorage.setItem('@MandeSeguro:user', JSON.stringify(userData));
            setAuthData({
                token: authData.token,
                user: userData,
            });
        },
        [authData],
    );

    const signOut = useCallback(() => {
        localStorage.removeItem('@MandeSeguro:token');
        localStorage.removeItem('@MandeSeguro:user');

        setAuthData({} as AuthState);
    }, []);
    return (
        <AuthContext.Provider
            value={{ user: authData.user, signIn, signOut, updateUser }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth(): AuthContextData {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within a AuthProvider');
    }

    return context;
}
